import React from "react";
import { useState } from "react";

import { makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ProcessStatusForm from "../../../components/forms/setup/processStatus/processStatusForm";
import { BannerNotificationType } from "../../../components/SimpleBanner";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import { generateUniqueNumber } from "../../../shared/utility";

export interface ProcessStatus {
  id: number;
  name: string;
  description?: string | null;
  created_at?: string | null;
}

export function InProcessMonitoringStatusesViewModel(
  logout: () => void,
  addSnackbar: (snackbar: any) => void,
) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null as any,
  });
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState<{ data: ProcessStatus[] }>(
    {} as { data: ProcessStatus[] },
  );

  const [slideOver, setSlideOver] = useState({
    childComponent: null as any,
    open: false,
  });

  const showErrorModal = (title: string, subtitle: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const getProcessStatuses = async function (search?: string, page?: number) {
    setLoading(true);
    const payload: any = { page: page, search: search };

    const callResult: any = await makePostApiCall(`processStatuses`, payload);
    setLoading(false);
    if (callResult.success) {
      setStatuses(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
  };

  const createProcessStatus = async function (values: any) {
    const payload = { ...values };
    const callResult: any = await makePostApiCall(
      "createProcessStatus",
      payload,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Process status added",
          SnackbarType.Success,
        ),
      );

      setSlideOver({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getProcessStatuses("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
  };

  const updateProcessStatus = async function (
    status: ProcessStatus,
    values: any,
    index?: number,
  ) {
    const callResult: any = await makePutApiCall(
      `processStatus/update/${status.id}`,
      values,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Process status updated",
          SnackbarType.Success,
        ),
      );

      setSlideOver({ childComponent: null, open: false });
      setTimeout(function () {
        getProcessStatuses("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
  };

  const deleteProcessStatus = async function (id: number, index?: number) {
    setLoading(true);

    const callResult: any = await makePostApiCall(
      `deleteProcessStatus/${id}`,
      {},
    );
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({ open: false, childComponent: null });
      getProcessStatuses("", 1);

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Process status deleted",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
  };

  const askDeleteProcessStatus = function (
    status: ProcessStatus,
    index?: number,
  ) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteProcessStatus(status.id, index)}
          message={`Are you sure you want to delete ${status.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const openAddProcessStatusSlideOver = () => {
    setSlideOver({
      childComponent: (
        <ProcessStatusForm
          isEdit={false}
          setOpen={setSlideOver}
          actionCall={createProcessStatus}
        />
      ),
      open: true,
    });
  };

  const viewProcessStatusDetails = async function (
    status: ProcessStatus,
    index?: number,
  ) {
    setSlideOver({
      childComponent: (
        <ProcessStatusForm
          status={status}
          index={index}
          isEdit={true}
          setOpen={setSlideOver}
          actionCall={updateProcessStatus}
        />
      ),
      open: true,
    });
  };

  return {
    askDeleteProcessStatus,
    emptyModal,
    setEmptyModelOpen,
    createProcessStatus,
    getProcessStatuses,
    loading,
    statuses,
    openAddProcessStatusSlideOver,
    setSlideOver,
    slideOver,
    updateProcessStatus,
    viewProcessStatusDetails,
  };
}

export default InProcessMonitoringStatusesViewModel;
